import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

import { Card, Icon, SearchBar } from 'components';
import { CardProps } from 'components/Card';
import { useLink } from 'hooks';
import { ClearButton, SearchIconContainer } from './styled';

type LayoutCardDesktopProps = {
  search?: {
    searchValue: string;
    onSearchValueChange: (value: string) => void;
  };
  title?: React.ReactNode;
  titleLink?:
    | {
        title: React.ReactNode;
        linkPath: string;
      }
    | false
    | null;
  titleExtra?: React.ReactNode;
  titleExtraPosition?: 'left' | 'right';
};

const LayoutCardDesktop: React.FC<
  Omit<CardProps, 'type'> & LayoutCardDesktopProps
> = ({
  search,
  title,
  titleExtra,
  titleExtraPosition = 'right',
  titleLink,
  children,
  ...props
}) => {
  const [isSearchBarOpen, setIsSearchBarOpen] = useState(false);
  const linkTo = useLink();
  const searchBarRef = useRef(null);

  useEffect(() => {
    if (isSearchBarOpen && searchBarRef.current) {
      searchBarRef.current.focus();
    }
  }, [isSearchBarOpen]);

  let titleNode: React.ReactNode = title;
  if (titleLink) {
    titleNode = (
      <TitleLink onClick={() => linkTo(titleLink.linkPath)}>
        {titleLink.title}
      </TitleLink>
    );
  }

  return (
    <CardDesktop
      {...props}
      type="secondary"
      title={
        <>
          <TitleContainer>
            <TitleTextContainer>{titleNode}</TitleTextContainer>
            {titleExtra && titleExtraPosition === 'left' && (
              <StyledTitleExtraContainer $position="left">
                {titleExtra}
              </StyledTitleExtraContainer>
            )}
            {search && !isSearchBarOpen && (
              <SearchIconContainer
                onClick={() => setIsSearchBarOpen(true)}
                data-cy="open-search-button"
              >
                <Icon type="search" color="gray10" offset={[0, 2]} />
              </SearchIconContainer>
            )}
            {titleExtra && titleExtraPosition === 'right' && (
              <StyledTitleExtraContainer $position="right">
                {titleExtra}
              </StyledTitleExtraContainer>
            )}
          </TitleContainer>
          {search && isSearchBarOpen && (
            <SearchBar
              ref={searchBarRef}
              value={search.searchValue}
              onChange={search.onSearchValueChange}
              placeholder="Search Here"
              suffix={
                <ClearButton
                  onClick={() => {
                    setIsSearchBarOpen(false);
                    search.onSearchValueChange('');
                  }}
                  data-cy="close-search-button"
                >
                  <Icon type="x" />
                </ClearButton>
              }
            />
          )}
        </>
      }
    >
      {children}
    </CardDesktop>
  );
};

export default LayoutCardDesktop;

const CardDesktop = styled(Card)`
  width: 281px;
  height: 480px;
  border-radius: 8px;

  margin-right: 20px;
  margin-bottom: 20px;

  display: flex;
  flex-direction: column;

  .ant-card-head {
    padding: 0;
    min-height: auto;

    .ant-card-head-wrapper {
      margin: 0 16px 4px 16px;

      .ant-card-head-title {
        padding-top: 14px;
      }
    }

    .ant-tabs-tab {
      margin: 0;
      padding: 7px 16px;
    }
  }

  .ant-card-body {
    padding: 0;
    flex-grow: 1;
    overflow-y: auto;
  }
`;

const TitleContainer = styled.div`
  display: flex;
  margin-bottom: 4px;
`;

const TitleTextContainer = styled.div`
  flex-grow: 1;
  font-weight: 600;
`;

const TitleLink = styled.span`
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
`;

const StyledTitleExtraContainer = styled.div<{ $position: 'left' | 'right' }>`
  ${({ $position }) =>
    $position === 'left' ? 'margin-right: 8px;' : 'margin-left: 14px;'}
`;
